import { doc, getDoc, setDoc, collection, query, where, getDocs, orderBy, deleteDoc } from 'firebase/firestore';

// Get the app ID from environment variables
// This will be used to construct the Firestore paths
const APP_ID = process.env.REACT_APP_FIREBASE_APP_ID || 'default-app-id';

/**
 * Retrieves the user's profile from Firestore.
 * @param {object} db - The Firestore database instance.
 * @param {string} userId - The Firebase user ID.
 * @returns {Promise<object|null>} The user profile data or null if not found.
 */
export const getUserProfile = async (db, userId) => {
    if (!db || !userId) {
        console.error("Firestore DB or User ID is missing for getUserProfile.");
        return null;
    }
    try {
        // Construct the document path for private user data
        const userDocRef = doc(db, `artifacts/${APP_ID}/users/${userId}/profile`, 'userProfile');
        const docSnap = await getDoc(userDocRef);
        if (docSnap.exists()) {
            return docSnap.data();
        } else {
            return null;
        }
    } catch (error) {
        console.error("Error fetching user profile:", error);
        return null;
    }
};

/**
 * Sets or updates the user's profile in Firestore.
 * @param {object} db - The Firestore database instance.
 * @param {string} userId - The Firebase user ID.
 * @param {object} profileData - The data to set/update in the user profile.
 * @returns {Promise<boolean>} True if successful, false otherwise.
 */
export const setUserProfile = async (db, userId, profileData) => {
    if (!db || !userId || !profileData) {
        console.error("Firestore DB, User ID, or Profile Data is missing for setUserProfile.");
        return false;
    }
    try {
        const userDocRef = doc(db, `artifacts/${APP_ID}/users/${userId}/profile`, 'userProfile');
        await setDoc(userDocRef, profileData, { merge: true }); // Use merge to update existing fields
        console.log("User profile updated successfully for userId:", userId);
        return true;
    } catch (error) {
        console.error("Error setting user profile:", error);
        return false;
    }
};

/**
 * Adds a new video entry to Firestore.
 * @param {object} db - The Firestore database instance.
 * @param {string} userId - The Firebase user ID of the uploader.
 * @param {string} xUsername - The X username of the uploader.
 * @param {object} videoData - The video data (title, url, category, etc.).
 * @returns {Promise<boolean>} True if successful, false otherwise.
 */
export const addVideo = async (db, userId, xUsername, videoData) => {
    if (!db || !userId || !xUsername || !videoData) {
        console.error("Missing data for addVideo.");
        return false;
    }
    try {
        // Store videos in a public collection so all users can see them
        const videoCollectionRef = collection(db, `artifacts/${APP_ID}/public/data/videos`);
        await setDoc(doc(videoCollectionRef), {
            ...videoData,
            uploaderId: userId, // Store the anonymous user ID
            uploaderXUsername: xUsername, // Store the X username
            timestamp: new Date(), // Add a timestamp for ordering
        });
        console.log("Video added successfully!");
        return true;
    } catch (error) {
        console.error("Error adding video:", error);
        return false;
    }
};

/**
 * Fetches videos from Firestore based on search query and category.
 * @param {object} db - The Firestore database instance.
 * @param {string} searchQuery - The search term for title or username.
 * @param {string} selectedCategory - The selected video category.
 * @returns {Promise<Array<object>>} An array of video documents.
 */
export const getVideos = async (db, searchQuery = '', selectedCategory = 'All') => {
    if (!db) {
        console.error("Firestore DB is missing for getVideos.");
        return [];
    }
    try {
        const videoCollectionRef = collection(db, `artifacts/${APP_ID}/public/data/videos`);
        let q = videoCollectionRef;

        // Apply category filter if not 'All'
        if (selectedCategory !== 'All') {
            q = query(q, where('category', '==', selectedCategory));
        }

        // Fetch all documents and then filter/sort in memory
        // Firestore orderBy() can lead to index missing errors, so we handle sorting client-side
        const querySnapshot = await getDocs(q);
        let videos = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Filter by search query (case-insensitive)
        if (searchQuery) {
            const lowerCaseQuery = searchQuery.toLowerCase();
            videos = videos.filter(video =>
                video.title.toLowerCase().includes(lowerCaseQuery) ||
                video.uploaderXUsername.toLowerCase().includes(lowerCaseQuery)
            );
        }

        // Sort by timestamp in descending order (latest first)
        videos.sort((a, b) => b.timestamp.toDate() - a.timestamp.toDate());

        return videos;
    } catch (error) {
        console.error("Error fetching videos:", error);
        return [];
    }
};

/**
 * Deletes a video from Firestore.
 * @param {object} db - The Firestore database instance.
 * @param {string} videoId - The ID of the video to delete.
 * @returns {Promise<boolean>} True if successful, false otherwise.
 */
export const deleteVideo = async (db, videoId) => {
    if (!db || !videoId) {
        console.error("Firestore DB or Video ID is missing for deleteVideo.");
        return false;
    }
    try {
        const videoDocRef = doc(db, `artifacts/${APP_ID}/public/data/videos`, videoId);
        await deleteDoc(videoDocRef);
        console.log("Video deleted successfully:", videoId);
        return true;
    } catch (error) {
        console.error("Error deleting video:", error);
        return false;
    }
};
